/**
 * Fait : la partie DOCUMENTS de la fiche d'un projet — la liste de ce qui a
 *   été déposé, où en est la lecture de chacun, le texte extrait qu'on ouvre
 *   et qu'on referme, la suppression derrière une fenêtre de confirmation, et
 *   le formulaire de dépôt pour qui peut modifier.
 *
 *   ELLE CHARGE SA PROPRE LISTE, et se recharge quand le direct le lui dit —
 *   le parent lui passe un compteur qui change à chaque événement du projet.
 *   Les états d'un document changent SEULS, en tâche de fond : sans le direct,
 *   « Lecture en cours » resterait à l'écran jusqu'à un rechargement à la main.
 *
 * Ne fait pas : elle ne réécrit aucune règle de droit — le serveur refuse la
 *   lecture seule, et un bouton caché n'est pas une garde — et n'extrait rien :
 *   le texte lui est rendu tout fait.
 */

import { useEffect, useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { FenetreDeConfirmation } from '../gabarits/FenetreDeConfirmation.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import {
  listeLesDocuments,
  litLeTexteDuDocument,
  supprimeLeDocument,
  type DocumentVu,
} from './api-documents.ts';
import { DepotDeDocument } from './DepotDeDocument.tsx';
import { useFraicheur } from './fraicheur.ts';
import { MOT_DE_L_ETAT, motDeLEchec, tailleLisible } from './mots-des-documents.ts';

/** Le texte ouvert à l'écran — un seul à la fois, celui qu'on vient de demander. */
interface TexteOuvert {
  readonly id: string;
  readonly nom: string;
  readonly contenu: string;
  readonly pages: number;
  readonly coupe: boolean;
}

/** « 1 page », « 12 pages » — le pluriel se pose, il ne se devine pas. */
function enPages(pages: number): string {
  return pages <= 1 ? `${pages} page` : `${pages} pages`;
}

export function SectionDocuments({
  projetId,
  peutModifier,
  tic,
}: {
  readonly projetId: string;

  /** Déposer et supprimer : ce que le serveur réserve à qui peut modifier. */
  readonly peutModifier: boolean;

  /** Change à chaque événement du projet — c'est le signal de rechargement. */
  readonly tic: number;
}) {
  const [documents, setDocuments] = useState<DocumentVu[]>([]);
  const [echec, setEchec] = useState<string | null>(null);
  const [texte, setTexte] = useState<TexteOuvert | null>(null);

  // Réponse périmée : voir `fraicheur.ts`, qui porte la règle et sa raison.
  const fraicheur = useFraicheur(projetId);

  /** Recharge la liste des documents du projet. */
  async function recharge(): Promise<void> {
    const pour = fraicheur.demandee;
    const issue = await listeLesDocuments(pour);

    if (!fraicheur.encoreValable(pour)) return;

    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    setDocuments(issue.valeur.fichiers);
    setEchec(null);

    /*
     * LA PORTE JUMELLE, fermée le 2026-08-25. Quand on supprime SOI-MÊME, la
     * fonction juste en dessous referme le texte ouvert. Quand quelqu'un
     * d'AUTRE supprime, le direct recharge la liste et la ligne disparaît —
     * mais le panneau de texte restait affiché, son titre avec : on lisait le
     * contenu d'un document qui n'existait plus. Mesuré par une épreuve
     * indépendante. La liste est la seule vérité : c'est donc ici qu'on
     * referme, et non dans chacun des gestes qui font disparaître une ligne.
     */
    setTexte((ouvert) =>
      ouvert !== null && !issue.valeur.fichiers.some((fichier) => fichier.id === ouvert.id)
        ? null
        : ouvert,
    );
  }

  // Changer de projet remet la partie à ZÉRO : sans cela, les documents de
  // l'ancien restent affichés le temps que la nouvelle liste arrive.
  useEffect(() => {
    setDocuments([]);
    setTexte(null);
    setEchec(null);
  }, [projetId]);

  useEffect(() => {
    void recharge();
  }, [projetId, tic]);

  /** Ouvre le texte d'un document prêt — le serveur refuse les autres. */
  async function ouvreLeTexte(document: DocumentVu): Promise<void> {
    const issue = await litLeTexteDuDocument(projetId, document.id);

    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    setTexte({
      id: document.id,
      nom: document.nomFichier,
      contenu: issue.valeur.texte,
      pages: issue.valeur.pages,
      coupe: issue.valeur.coupe,
    });
  }

  /** Supprime un document, puis recharge — le direct prévient les autres. */
  async function supprime(document: DocumentVu): Promise<void> {
    const issue = await supprimeLeDocument(projetId, document.id);

    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    if (texte?.id === document.id) setTexte(null);
    await recharge();
  }

  return (
    <Partie
      etiquette="Ce qui a été déposé"
      nature="travail"
      titre="Documents"
      aide="Un document déposé est lu par l’atelier, puis son texte sert aux conversations du projet."
      data-role="partie-documents"
    >
      {echec !== null && (
        <span data-role="echec-des-documents">
          <Annonce urgence="urgent" nature="refus">
            {echec}
          </Annonce>
        </span>
      )}

      <ul className="rangees" data-liste="documents">
        {documents.map((document) => (
          <li key={document.id} data-document={document.id}>
            <span data-role="nom-du-document">{document.nomFichier}</span>{' '}
            <span className="texte-attenue">({tailleLisible(document.tailleOctets)})</span>{' '}
            <span data-role="etat-du-document">{MOT_DE_L_ETAT[document.etat]}</span>
            {document.etat === 'pret' && document.pages !== undefined && (
              <span className="texte-attenue"> — {enPages(document.pages)}</span>
            )}
            {document.etat === 'refuse' && (
              <span data-role="echec-du-document" className="message-erreur">
                {' '}
                {motDeLEchec(document.natureDEchec)}
              </span>
            )}
            {document.etat === 'pret' && (
              <button
                type="button"
                data-role="lire-le-texte"
                onClick={() => void ouvreLeTexte(document)}
              >
                Lire le texte
              </button>
            )}
            {peutModifier && (
              <FenetreDeConfirmation
                libelle="Supprimer"
                titre={`Supprimer « ${document.nomFichier} » ?`}
                libelleDeConfirmation="Supprimer ce document"
                data-role="supprimer-le-document"
                surConfirmation={() => void supprime(document)}
              >
                <p>
                  Le document et le texte qui en a été tiré partent pour de bon. Rien ne les
                  rattrape.
                </p>
                <p>Les conversations déjà tenues, elles, restent telles quelles.</p>
              </FenetreDeConfirmation>
            )}
          </li>
        ))}

        {documents.length === 0 && <li data-etat="vide">Aucun document pour l’instant.</li>}
      </ul>

      {texte !== null && (
        <div data-role="texte-du-document">
          <h4>Texte de {texte.nom}</h4>

          <p className="texte-attenue">{enPages(texte.pages)} lue(s) par l’atelier.</p>

          {texte.coupe && (
            <Annonce urgence="poli">
              Ce texte est très long : seul son début a été gardé. La suite n’est pas perdue, elle
              n’est simplement pas lue par l’atelier.
            </Annonce>
          )}

          <p className="texte-du-document">{texte.contenu}</p>

          <button type="button" data-role="masquer-le-texte" onClick={() => setTexte(null)}>
            Masquer le texte
          </button>
        </div>
      )}

      {peutModifier && <DepotDeDocument projetId={projetId} surDepot={() => void recharge()} />}
    </Partie>
  );
}
